import { agreementSigned } from '../domain/agreement';
import { isInspectionLocked, type V2Inspection } from '../domain/inspection';
import { accountGuard } from '../persistence/accountScope';
import { getAgreementPdf, putAgreementPdf, type StoredAgreementPdf } from '../persistence/v2Database';
import { renderAgreementDocumentHtml } from '../report/agreementHtml';
import type { PdfRenderer } from '../report/pdf';
import { createStore } from './createStore';
import { formalEligibilityBlockedFor } from './formalWorkflow';
import { inspectionStore } from './inspectionStore';

// Owner 2026-10-21: 「双方签完就生成并存好协议 PDF；出报告时直接用、不用再预览；签完后协议有变化（比如客户
// 撤回 AI 同意）才重新生成。」
//
// As soon as both parties have signed, the agreement is rendered once, in the background, and
// kept in the media store. Its FINGERPRINT is the agreement's HTML with the generation time left out —
// exactly what the PDF says. A change that alters the printed agreement after signing (the client
// withdrawing the AI consent, the inspection date, the address …) changes the fingerprint and the PDF is
// rendered again; anything else (rows, photos, findings) leaves it alone. The final report uses the kept
// PDF as its agreement output (src/state/reportStore.ts) and sharing from Inspection Details sends it
// (src/state/agreementPdf.ts); neither renders it again when it is current.
export interface AgreementPdfEntry {
  fingerprint: string;
  pageCount: number;
  generatedAt: number;
}

interface AgreementPdfState {
  // inspectionId → the PDF kept for it (only what this session has seen or made)
  entries: Record<string, AgreementPdfEntry>;
  // inspectionId → the fingerprint whose rendering failed; tried again when the agreement changes, the app
  // restarts or the inspector taps 再试一次. iPhone real-photo run 2026-10-26: a failure used to be silent and
  // the Report screen went on saying 「不用预览」 over an agreement PDF that did not exist.
  failed: Record<string, string>;
  // the inspection whose agreement is being drawn right now
  drawing: string | null;
}

export const agreementPdfStore = createStore<AgreementPdfState>({ entries: {}, failed: {}, drawing: null });

const fingerprints = new WeakMap<V2Inspection, string>();

// Two independent 32-bit hashes and the length: a collision would need all three to agree.
function hashText(text: string): string {
  let a = 0x811c9dc5;
  let b = 5381;
  for (let i = 0; i < text.length; i += 1) {
    const c = text.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193);
    b = (Math.imul(b, 33) + c) | 0;
  }
  return `${(a >>> 0).toString(16).padStart(8, '0')}${(b >>> 0).toString(16).padStart(8, '0')}-${text.length}`;
}

export function agreementPdfFingerprint(inspection: V2Inspection): string {
  const known = fingerprints.get(inspection);
  if (known) return known;
  const value = hashText(renderAgreementDocumentHtml(inspection, 0));
  fingerprints.set(inspection, value);
  return value;
}

// Which inspections keep one: signed, still open on this device, in the supported workflow.
function wantsAgreementPdf(inspection: V2Inspection): boolean {
  return !isInspectionLocked(inspection) && !inspection.movedOut && agreementSigned(inspection.agreement) && !formalEligibilityBlockedFor(inspection);
}

// True when the kept PDF says exactly what the agreement says now (the Report screen's 「签署后已生成」).
export function agreementPdfReady(inspection: V2Inspection, state: AgreementPdfState = agreementPdfStore.get()): boolean {
  const entry = state.entries[inspection.id];
  return entry !== undefined && entry.fingerprint === agreementPdfFingerprint(inspection);
}

// True when the PDF of the agreement as it stands now could not be made (the Report screen and the
// agreement card say so and offer 再试一次).
export function agreementPdfFailed(inspection: V2Inspection, state: AgreementPdfState = agreementPdfStore.get()): boolean {
  return state.failed[inspection.id] === agreementPdfFingerprint(inspection) && !agreementPdfReady(inspection, state);
}

function withoutFailure(failed: Record<string, string>, inspectionId: string): Record<string, string> {
  if (!(inspectionId in failed)) return failed;
  const next = { ...failed };
  delete next[inspectionId];
  return next;
}

function remember(inspectionId: string, entry: AgreementPdfEntry): void {
  agreementPdfStore.set((prev) => ({ ...prev, entries: { ...prev.entries, [inspectionId]: entry }, failed: withoutFailure(prev.failed, inspectionId) }));
}

async function readStored(inspectionId: string): Promise<StoredAgreementPdf | null> {
  try {
    return await getAgreementPdf(inspectionId);
  } catch {
    return null;
  }
}

// ---------- the background keeper (started once from src/main.tsx) ----------

let keeperRenderer: PdfRenderer | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let pass: Promise<void> | null = null;
let again = false;
let inFlight: { inspectionId: string; done: Promise<void> } | null = null;

async function keepOne(inspection: V2Inspection, renderer: PdfRenderer): Promise<void> {
  const fingerprint = agreementPdfFingerprint(inspection);
  const known = agreementPdfStore.get();
  if (known.entries[inspection.id]?.fingerprint === fingerprint || known.failed[inspection.id] === fingerprint) return;
  const stored = await readStored(inspection.id);
  if (stored && stored.fingerprint === fingerprint) {
    remember(inspection.id, { fingerprint, pageCount: stored.pageCount, generatedAt: stored.generatedAt });
    return;
  }
  const sameAccount = accountGuard();
  const generatedAt = Date.now();
  agreementPdfStore.set((prev) => ({ ...prev, drawing: inspection.id }));
  try {
    const pdf = await renderer.render(renderAgreementDocumentHtml(inspection, generatedAt));
    if (!sameAccount()) return;
    // Changed while it was being drawn: the next pass draws the new one.
    // Finalized meanwhile: the report took the agreement over, nothing is kept.
    const now = inspectionStore.get().inspections.find((i) => i.id === inspection.id);
    if (!now || !wantsAgreementPdf(now) || agreementPdfFingerprint(now) !== fingerprint) return;
    await putAgreementPdf(inspection.id, { type: 'application/pdf', bytes: pdf.bytes, fingerprint, pageCount: pdf.pageCount, generatedAt });
    if (!sameAccount()) return;
    remember(inspection.id, { fingerprint, pageCount: pdf.pageCount, generatedAt });
  } catch {
    if (sameAccount()) agreementPdfStore.set((prev) => ({ ...prev, failed: { ...prev.failed, [inspection.id]: fingerprint } }));
  } finally {
    agreementPdfStore.set((prev) => (prev.drawing === inspection.id ? { ...prev, drawing: null } : prev));
  }
}

// The inspector tapped 再试一次: forget the failure and draw it now.
export function retryAgreementPdf(inspectionId: string): void {
  agreementPdfStore.set((prev) => ({ ...prev, failed: withoutFailure(prev.failed, inspectionId) }));
  if (keeperRenderer) kick();
}

async function runPass(): Promise<void> {
  do {
    again = false;
    const renderer = keeperRenderer;
    if (!renderer) return;
    for (const inspection of inspectionStore.get().inspections) {
      if (!wantsAgreementPdf(inspection)) continue;
      const done = keepOne(inspection, renderer);
      inFlight = { inspectionId: inspection.id, done };
      await done;
      inFlight = null;
    }
  } while (again);
}

function kick(): void {
  if (pass) {
    again = true;
    return;
  }
  pass = runPass().finally(() => {
    pass = null;
  });
}

export function startAgreementPdfKeeper(renderer: PdfRenderer, delayMs = 1500): () => void {
  keeperRenderer = renderer;
  const schedule = () => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      kick();
    }, delayMs);
  };
  const unsubscribe = inspectionStore.subscribe(schedule);
  schedule();
  return () => {
    unsubscribe();
    if (timer !== null) clearTimeout(timer);
    timer = null;
    keeperRenderer = null;
  };
}

// The kept PDF of this inspection when it still says what the agreement says now; null otherwise (the
// caller renders it). Waits for the keeper if it is drawing this very agreement at the moment.
export async function currentAgreementPdf(inspection: V2Inspection): Promise<StoredAgreementPdf | null> {
  if (inFlight?.inspectionId === inspection.id) await inFlight.done.catch(() => undefined);
  const stored = await readStored(inspection.id);
  return stored && stored.fingerprint === agreementPdfFingerprint(inspection) ? stored : null;
}

export function resetAgreementPdfStore(): void {
  agreementPdfStore.set(() => ({ entries: {}, failed: {}, drawing: null }));
}
